import { FeatureFlag } from '@ps/web-core-enums';
import type { CashoutQuote, MyBetsResponse } from '@ps/web-sports-api-types';

import type { WidgetEffectParams } from '@swf/core/store';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';

import type { SportsMyBetsWidgetConfig } from '../config';

import type { SearchBetsQuery, WidgetDispatch, WidgetSelect } from './types';

const PAGE_SIZE = 6;
const TO_RECORD_OFFSET = PAGE_SIZE - 1;

export const effects = {
  async betsRequested(
    payload: SearchBetsQuery,
    params: WidgetEffectParams<SportsMyBetsWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, select, rootState, widget } = params;
    const widgetSelect: WidgetSelect = select[widget.uid];
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      let { fromRecord } = payload;

      // if the fromRecord isn't provided, calculate it from the local state
      if (fromRecord === undefined) {
        fromRecord = widgetSelect.bets(rootState).length + 1;
      }

      const result = await sportsRequest<MyBetsResponse>({
        path: '/my-bets',
        includeFeatures: [FeatureFlag.ISPEnableStarSub, FeatureFlag.ISPEnableEnhancedBetTracking],
        body: {
          fromRecord,
          toRecord: payload.toRecord ?? fromRecord + TO_RECORD_OFFSET,
          ...payload
        },
        signal: getWidgetCancellationSignal(params)
      });

      widgetDispatch.betsLoadedSuccessfully({
        ...result.body,
        isSettled: payload.isSettled
      });
    } catch (error) {
      widgetDispatch.betsLoadedUnsuccessfully();
    }
  },

  async cashOutQuotesRequested(
    _payload: undefined,
    params: WidgetEffectParams<SportsMyBetsWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, select, rootState, widget } = params;
    const widgetSelect: WidgetSelect = select[widget.uid];
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];

    const sportsRequest = buildSportsRequest(request, rootState);

    const betIds = Object.keys(widgetSelect.cashOutQuotes(rootState)).join();
    try {
      const result = await sportsRequest<Record<string, CashoutQuote>>({
        path: '/gbp/fcq/api/v0.1/getBetQuotes',
        body: {
          betIds
        },
        signal: getWidgetCancellationSignal(params)
      });

      widgetDispatch.cashOutQuotesUpdated(result.body);
    } catch (_) {
      // do nothing
    }
  }
};
